'use client'

// The Social tab, live.
//
// "Alkanes" is Twetch's exact smart-tag feed — real posts, real authors, real
// counts, no auth needed to read. It never infers membership from ticker text.
// The identity door above it (twetch-door)
// carries connect → join → compose. "Following" stays a LABELLED placeholder:
// there is no follow graph for wallet-rooted accounts yet, and an empty tab
// pretending otherwise would be the exact invented-fact failure this app
// exists to avoid.
//
// Row anatomy is the fomo blend the placeholder was drawn in: avatar + bold
// name + quiet timestamp, the post body, media, then the reaction counts as
// read-only figures — likes and replies are numbers Twetch reports, not
// actions this app can take yet (external-account reactions are deferred
// server-side). Every row links out to its twetch.com permalink for the full
// experience.

import React from 'react'
import useSwr from 'swr'
import { ArrowLeft, Heart, MessageCircle } from 'lucide-react'
import { cn } from '@/lib/utils'
import event from '@/utils/event'
import Wallet from '@/utils/wallet'
import { useAlkanes } from '../lib/store'
import { OW_NAMESPACE, slugFromTagId } from '../lib/collection-tag'
import { useTwetch } from '../lib/twetch-store'
import useTwetchActions from '../lib/use-twetch-actions'
import { formatAge } from '../lib/format'
import { twetchJson } from '../lib/twetch-api'
import type { TwetchFeedPage, TwetchPost, TwetchSmartTag, TwetchUser } from '../lib/twetch-api'
import {
	contentMediaRefs,
	dimsCacheId,
	postFileRefs,
	postPermalink,
	pixelArtClass,
	resolveTwetchImage,
	stripContentMediaRefs
} from '../lib/twetch-media'
import { openTwetchUserProfile } from '../lib/twetch-profile'
import useTwetchFeed, { mergeFeedPages, type TwetchFeedData } from '../lib/use-twetch-feed'
import TwetchDoor from './twetch-door'
import UserHoverCard from './user-hover-card'
import useMarketBadge from '../lib/use-market-badge'
import { viewerProfileKey } from '../lib/use-viewer-profile'
import { POST_PARAM, parsePostParam, takeLinkedPost } from '../lib/linked-post'
import useLoggedIn from '@/utils/hooks/useLoggedIn'

// ── skeletons: the shape of a post, unmistakably not a post ────────────────

function Bar({ className }: { className?: string }) {
	return <span className={cn('block h-3 rounded bg-secondary', className)} aria-hidden="true" />
}

function GhostAvatar({ size = 36, className }: { size?: number; className?: string }) {
	return (
		<span
			aria-hidden="true"
			style={{ width: size, height: size }}
			className={cn('block shrink-0 rounded-full bg-secondary', className)}
		/>
	)
}

/// Loading skeleton shaped like the row that replaces it.
function GhostPost() {
	return (
		<div className="px-4 py-3.5" aria-hidden="true">
			<div className="flex items-center gap-2.5">
				<GhostAvatar />
				<span className="flex min-w-0 flex-1 items-center gap-1.5">
					<Bar className="w-24" />
				</span>
				<Bar className="w-8" />
			</div>
			<div className="ml-[46px] mt-2 space-y-1.5">
				<Bar className="w-4/5" />
				<Bar className="w-3/5" />
			</div>
		</div>
	)
}

// ── one post row ───────────────────────────────────────────────────────────

function initials(name: string | null | undefined, userId: number): string {
	const n = (name ?? '').trim()
	return n ? n.slice(0, 2).toUpperCase() : String(userId % 100)
}

export function Avatar({
	user,
	userId,
	size = 36
}: {
	user?: TwetchUser
	userId: number
	size?: number
}) {
	const [broken, setBroken] = React.useState(false)
	const src = resolveTwetchImage(user?.icon)
	if (!src || broken) {
		return (
			<span
				aria-hidden="true"
				style={{ width: size, height: size }}
				className="grid shrink-0 place-items-center rounded-full bg-muted text-xxs font-semibold text-muted-foreground"
			>
				{initials(user?.name, userId)}
			</span>
		)
	}
	return (
		<img
			src={src}
			alt=""
			width={size}
			height={size}
			loading="lazy"
			onError={() => setBroken(true)}
			style={{ width: size, height: size }}
			className={`shrink-0 rounded-full object-cover ${pixelArtClass(src)}`}
		/>
	)
}

function MediaGrid({ post, dims }: { post: TwetchPost; dims: Record<string, [number, number]> }) {
	const refs = React.useMemo(() => {
		const all = [...postFileRefs(post.files), ...contentMediaRefs(post.content)]
		const urls: { url: string; ratio: number | null }[] = []
		const seen = new Set<string>()
		for (const ref of all) {
			const url = resolveTwetchImage(ref)
			if (!url || seen.has(url)) {
				continue
			}
			seen.add(url)
			const cacheId = dimsCacheId(ref)
			const wh = cacheId ? dims[cacheId] : undefined
			urls.push({ url, ratio: wh && wh[0] > 0 && wh[1] > 0 ? wh[0] / wh[1] : null })
		}
		return urls.slice(0, 4)
	}, [post.files, post.content, dims])

	const [broken, setBroken] = React.useState<Record<string, boolean>>({})
	const live = refs.filter((r) => !broken[r.url])
	if (live.length === 0) {
		return null
	}
	return (
		<div className={cn('mt-2 gap-1.5', live.length > 1 ? 'grid grid-cols-2' : 'block')}>
			{live.map(({ url, ratio }) => (
				<img
					key={url}
					src={url}
					alt=""
					loading="lazy"
					onError={() => setBroken((b) => ({ ...b, [url]: true }))}
					style={
						// The server pre-measured most media; sizing from those dims
						// means the row never grows after the image paints. Unmeasured
						// media caps its height instead of gambling on layout.
						ratio && live.length === 1 ? { aspectRatio: String(ratio) } : undefined
					}
					className={cn(
						'w-full rounded-xl bg-secondary/40 object-cover',
						live.length > 1 ? 'aspect-square' : 'max-h-[420px]'
					)}
				/>
			))}
		</div>
	)
}

/// A quoted post, compact: who said it and what, clamped.
function QuoteCard({ post, user }: { post: TwetchPost; user?: TwetchUser }) {
	return (
		<a
			href={postPermalink(post)}
			target="_blank"
			rel="noreferrer"
			className="mt-2 block rounded-xl border border-border/60 px-3 py-2.5 transition-colors hover:border-border"
		>
			<span className="flex items-center gap-2">
				<Avatar user={user} userId={post.userId} size={20} />
				<span className="truncate text-xs font-semibold">{user?.name ?? `#${post.userId}`}</span>
			</span>
			{post.content?.trim() && (
				<span className="mt-1 line-clamp-2 block whitespace-pre-wrap break-words text-xs leading-relaxed text-muted-foreground">
					{stripContentMediaRefs(post.content)}
				</span>
			)}
		</a>
	)
}

/// A count that is also the control: the figure Twetch reports, and the free
/// wallet-signed action that adds to it. A done action stays lit and inert —
/// the chain already has it.
function ActionCount({
	icon: Icon,
	value,
	label,
	doneLabel,
	active,
	busy,
	activeClassName,
	testId,
	unavailable,
	onAct
}: {
	/// Why this action isn't offered here (shown as the tooltip). The count
	/// still shows; the control just can't be pressed.
	unavailable?: string
	icon: typeof Heart
	value: number
	label: string
	doneLabel: string
	active: boolean
	busy: boolean
	activeClassName: string
	testId: string
	onAct: () => void
}) {
	return (
		<button
			type="button"
			data-testid={testId}
			aria-pressed={active}
			aria-label={`${unavailable ?? (active ? doneLabel : label)} · ${value}`}
			title={unavailable}
			disabled={busy || active || unavailable !== undefined}
			onClick={onAct}
			className={cn(
				'-m-1 inline-flex items-center gap-1.5 rounded-md p-1 text-muted-foreground transition-colors enabled:hover:bg-secondary/60 enabled:hover:text-foreground disabled:cursor-default',
				active && activeClassName,
				busy && 'animate-pulse'
			)}
		>
			<Icon className="size-4" aria-hidden="true" />
			<span className="text-xs tabular-nums">{value.toLocaleString('en-US')}</span>
		</button>
	)
}

/// A read-only count: a figure Twetch reports, not a button this app has.
function Count({
	icon: Icon,
	value,
	label
}: {
	icon: typeof Heart
	value: number | undefined
	label: string
}) {
	const n = value ?? 0
	return (
		// The digit renders even at zero: icon-only pairs read as buttons, and
		// these are figures Twetch reports — reacting from here isn't wired.
		<span
			className="inline-flex items-center gap-1.5 text-muted-foreground"
			aria-label={`${n} ${label}`}
		>
			<Icon className="size-4" aria-hidden="true" />
			<span className="text-xs tabular-nums">{n.toLocaleString('en-US')}</span>
		</span>
	)
}

function inTerminal(): boolean {
	return typeof window !== 'undefined' && window.location.pathname.startsWith('/alkanes')
}

/// Where a smart tag's market lives on this site.
function smartTagHref(tag: TwetchSmartTag): string {
	if (tag.namespace === OW_NAMESPACE) {
		const slug = slugFromTagId(tag.id)
		return slug ? `/collection/${encodeURIComponent(slug)}` : '#'
	}
	return `/alkanes/${tag.id}`
}

/// `@123` in a post is a user id (Twetch's mention form). Shown as the
/// person's name, resolved the way Twetch's own client does it: from the
/// page's user map when it has them, otherwise their profile read (the same
/// cache the profile page and hover card use).
function Mention({ userId, known }: { userId: number; known?: TwetchUser }) {
	const { data } = useSwr<TwetchUser>(
		known?.name ? null : viewerProfileKey(userId),
		() => twetchJson<TwetchUser>(`/v1/users/${userId}`),
		{ revalidateOnFocus: false, shouldRetryOnError: false }
	)
	const user = known?.name ? known : data
	return (
		<UserHoverCard userId={userId} user={user}>
			<button
				type="button"
				data-testid="alkanes-post-mention"
				onClick={(e) => {
					e.stopPropagation()
					void openTwetchUserProfile(userId)
				}}
				className="font-semibold text-primary hover:underline"
			>
				@{user?.name?.trim() || userId}
			</button>
		</UserHoverCard>
	)
}

/// The last characters of the wallet an account posts from — enough to tell
/// two people with the same display name apart, and to recognise your own
/// wallet, without spending the row's width on a full address.
const ADDRESS_TAIL = 6

function authorAddressTail(user: TwetchUser | undefined): string | null {
	const address = user?.externalIdentity?.address?.trim()
	return address && address.length > ADDRESS_TAIL ? address.slice(-ADDRESS_TAIL) : null
}

/// The word Twetch prints on these posts, printed here too: this one was
/// published as activity, not as an ordinary post.
function ThesisTag() {
	return (
		<span
			data-testid="post-thesis-tag"
			className="shrink-0 rounded-full bg-primary/10 px-1.5 py-0.5 text-xxs font-semibold leading-4 text-primary"
		>
			Thesis
		</span>
	)
}

/// The market a post is about, as its own face and name, reading as the first
/// thing the post says. A cashtag alone is derived text — our composers even
/// write it for the author — so the icon and the market's real name lead the
/// sentence instead, and the author's own words follow.
function MarketPill({ tag }: { tag: TwetchSmartTag }) {
	const badge = useMarketBadge(tag)
	const [broken, setBroken] = React.useState(false)
	return (
		<>
			<a
				href={badge?.href ?? smartTagHref(tag)}
				data-testid="post-market-link"
				onClick={(e) => e.stopPropagation()}
				title={badge?.name ?? `$${tag.symbol}`}
				// Quiet by design: the prose's cashtag is the accent on this row, so
				// attribution rides on the icon and a secondary surface instead of a
				// second purple chip saying the same word.
				// Sits ON the text's line: `align-middle` and a slightly tighter
				// leading keep the row's height the same as an untagged post's.
				className="mr-1.5 inline-flex max-w-full items-center gap-1.5 rounded-full bg-secondary/70 py-0.5 pl-0.5 pr-2 align-middle text-xxs font-semibold leading-4 text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
			>
				{badge?.icon && !broken ? (
					<img
						src={badge.icon}
						alt=""
						loading="lazy"
						onError={() => setBroken(true)}
						className="size-4 shrink-0 rounded-full object-cover [image-rendering:pixelated]"
					/>
				) : (
					<span className="grid size-4 shrink-0 place-items-center rounded-full bg-primary/15 text-[9px] font-bold text-primary">
						$
					</span>
				)}
				<span className="truncate">{badge?.name ?? `$${tag.symbol}`}</span>
			</a>
		</>
	)
}

export function PostText({
	text,
	smartTags = [],
	users
}: {
	text: string
	smartTags?: TwetchSmartTag[]
	/// Users the page already carries, so most mentions need no request.
	users?: Record<string, TwetchUser>
}) {
	const bySymbol = new Map(smartTags.map((tag) => [tag.symbol.toLowerCase(), tag]))
	const parts: React.ReactNode[] = []
	const pattern = /(^|\s)(?:\$([A-Za-z][A-Za-z0-9]{0,14})|@(\d{1,10})(?![\dA-Za-z_]))/g
	let last = 0
	let match: RegExpExecArray | null
	while ((match = pattern.exec(text))) {
		const start = match.index + match[1].length
		if (start > last) parts.push(text.slice(last, start))
		if (match[3] !== undefined) {
			const userId = Number(match[3])
			parts.push(<Mention key={`${start}-@${userId}`} userId={userId} known={users?.[String(userId)]} />)
			last = pattern.lastIndex
			continue
		}
		const tag = bySymbol.get(match[2].toLowerCase())
		parts.push(
			tag ? (
				<a
					key={`${start}-${tag.id}`}
					href={smartTagHref(tag)}
					onClick={(e) => {
						// Inside the terminal the store IS the router — a full
						// navigation would reload it to change one id. Anywhere
						// else (and for non-alkane markets) the href does the work;
						// it also serves middle-click, cmd-click and crawlers.
						if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) {
							return
						}
						if (tag.namespace !== 'alkanes' || !inTerminal()) {
							return
						}
						e.preventDefault()
						useAlkanes.getState().navigate('home', tag.id)
					}}
					className="font-semibold text-primary hover:underline"
					title={`${tag.namespace}:${tag.id}`}
				>
					${match[2]}
				</a>
			) : (
				`$${match[2]}`
			)
		)
		last = pattern.lastIndex
	}
	if (last < text.length) parts.push(text.slice(last))
	return <>{parts}</>
}

/// The lookup maps a row needs — a feed page and a replies page both have them.
type FeedLookups = Pick<
	TwetchFeedData,
	'users' | 'replyPosts' | 'quotes' | 'quotedPosts' | 'mediaDims'
>

function PostRow({
	post,
	feed,
	now,
	onOpen,
	lead = false,
	threadLine = false,
	hideReplyingTo = false,
	showMarket = false
}: {
	/// A feed that mixes markets (the homepage) names each post's market and
	/// links it; a market's own feed has no need to.
	showMarket?: boolean
	post: TwetchPost
	feed: FeedLookups
	now: number
	/// Open this post's conversation. Absent on the thread's own lead post.
	/// `viaReply` is true when the reader used the reply control, so the
	/// thread opens with the cursor in its reply box.
	onOpen?: (post: TwetchPost, viaReply?: boolean) => void
	/// The post a thread is about: a touch larger, never "Replying to".
	lead?: boolean
	/// Draw the conversation line from this avatar down to the next row — the
	/// row below is a reply to this one.
	threadLine?: boolean
	/// Inside a thread the chain itself says who is being answered.
	hideReplyingTo?: boolean
}) {
	const user = feed.users[String(post.userId)]
	const permalink = postPermalink(post)
	const postedAt = post.postedAtMs ?? post.createdAtMs
	const marketTag = post.smartTags?.[0]
	// A post about a market, written in a market's own feed, IS a thesis on it.
	// Keying this on `type === 'system'` labelled the TRANSPORT instead: the
	// same words published before the system-post path existed, or by a wallet
	// that could not reach it, arrived unlabelled beside identical ones that
	// could — a distinction the author never made and a reader cannot see.
	// A reply carries no market tag, so it is not one of these.
	const isThesis = marketTag !== undefined
	const addressTail = authorAddressTail(user)
	const replyTarget = post.replyPostId ? feed.replyPosts[String(post.replyPostId)] : undefined
	const replyAuthor = replyTarget ? feed.users[String(replyTarget.userId)] : undefined
	const quotedId = feed.quotes[String(post.id)]
	const quoted = quotedId !== undefined ? feed.quotedPosts[String(quotedId)] : undefined
	const body = post.content ? stripContentMediaRefs(post.content) : ''
	// Our composers prefix a thesis with its own `$TICKER`. When the pill above
	// already names that market, the prefix is the same word twice — drop it
	// from the reading copy (the post itself, on Twetch, is untouched).
	const prose = React.useMemo(() => {
		if (!showMarket || !marketTag) return body
		const lead = new RegExp(`^\\$${marketTag.symbol}(?![A-Za-z0-9])\\s*`, 'i')
		const trimmed = body.replace(lead, '')
		return trimmed.trim() ? trimmed : body
	}, [body, showMarket, marketTag])

	// Free, wallet-signed actions. Signed out, the same control is the door in.
	const actions = useTwetchActions(post.id)
	const loggedIn = !!useLoggedIn()
	const act = (run: () => void) => {
		if (!loggedIn) {
			event.emit('command', { id: 'connect-wallet' })
			return
		}
		run()
	}
	const rowError =
		actions.error &&
		actions.error.key === `like-${post.id}`
			? actions.error.message
			: null

	return (
		<li
			data-testid="alkanes-social-post"
			data-post-id={post.id}
			// The whole row opens the conversation; its own links and buttons
			// (author, media, permalink, cashtags) keep their own jobs.
			onClick={
				onOpen
					? (e) => {
							if ((e.target as HTMLElement).closest('a, button')) return
							onOpen(post)
					  }
					: undefined
			}
			className={cn(
				'relative px-4 py-3.5',
				onOpen && 'cursor-pointer transition-colors hover:bg-secondary/20 active:bg-secondary/30'
			)}
		>
			{threadLine && (
				// Avatar: 36px at the row's 16px gutter → its centre is x=34; the
				// line runs from under it to the row's bottom edge, where the next
				// row's avatar picks it up.
				<span
					aria-hidden="true"
					className="pointer-events-none absolute bottom-0 left-[33px] top-[54px] w-0.5 rounded-full bg-border"
				/>
			)}
			<div className="flex items-center gap-2.5">
				{/* Author → their profile HERE (the same page the swap tape and
				    holders open), not out to an explorer or another site. */}
				<UserHoverCard userId={post.userId} user={user}>
					<button
						type="button"
						data-testid="alkanes-post-author"
						onClick={() => void openTwetchUserProfile(post.userId)}
						aria-label={`Open ${user?.name ?? `#${post.userId}`}'s profile`}
						className="flex min-w-0 items-center gap-2.5 rounded-lg text-left transition-opacity hover:opacity-80"
					>
						<Avatar user={user} userId={post.userId} />
						<span className="min-w-0 truncate text-sm font-semibold hover:underline">
							{user?.name ?? `#${post.userId}`}
						</span>
						{addressTail && (
							<span
								data-testid="alkanes-post-author-address"
								title={user?.externalIdentity?.address ?? undefined}
								className="shrink-0 font-mono text-xxs text-muted-foreground"
							>
								…{addressTail}
							</span>
						)}
					</button>
				</UserHoverCard>
				{isThesis && <ThesisTag />}
				<span className="flex-1" aria-hidden="true" />
				{/* When it was posted, and the way out to it: one control, on the
				    row's far edge, instead of a time beside a second icon that
				    opened the same page. */}
				{postedAt !== undefined && (
					<a
						href={permalink}
						target="_blank"
						rel="noreferrer"
						onClick={(e) => e.stopPropagation()}
						title={`${new Date(postedAt).toLocaleString()} — open on Twetch`}
						className="alkanes-touch shrink-0 text-xxs tabular-nums text-muted-foreground transition-colors hover:text-foreground hover:underline"
					>
						{formatAge(Math.max(0, now - postedAt))} ago
					</a>
				)}
			</div>

			<div className="ml-[46px]">
				{replyTarget && !lead && !hideReplyingTo && (
					<span className="mt-0.5 block text-xxs text-muted-foreground">
						Replying to {replyAuthor?.name ?? `#${replyTarget.userId}`}
					</span>
				)}
				{(prose || (showMarket && marketTag)) && (
					<p
						className={cn(
							'mt-1 block whitespace-pre-wrap break-words leading-relaxed',
							lead ? 'text-base' : 'text-sm'
						)}
					>
						{showMarket && marketTag && <MarketPill tag={marketTag} />}
						<PostText text={prose} smartTags={post.smartTags} users={feed.users} />
					</p>
				)}
				{post.contentTruncated && (
					<a
						href={permalink}
						target="_blank"
						rel="noreferrer"
						className="mt-0.5 inline-block text-xs text-primary hover:underline"
					>
						Read the rest on Twetch
					</a>
				)}
				<MediaGrid post={post} dims={feed.mediaDims} />
				{quoted && <QuoteCard post={quoted} user={feed.users[String(quoted.userId)]} />}
				{!prose && !quoted && post.linkPreview?.title && post.linkPreview?.url && (
					<a
						href={post.linkPreview.url}
						target="_blank"
						rel="noreferrer"
						className="mt-1.5 block truncate text-xs text-primary hover:underline"
					>
						{post.linkPreview.title}
					</a>
				)}

				<div className="mt-2.5 flex items-center gap-6">
					<ActionCount
						icon={Heart}
						value={actions.likeCount(post.id, post.numLikes)}
						label="Like"
						doneLabel="Liked"
						active={actions.isLiked(post.id)}
						busy={actions.busyKey === `like-${post.id}`}
						activeClassName="text-destructive-ink [&_svg]:fill-current"
						testId="alkanes-post-like"
						unavailable={
							actions.viewerId !== null && actions.viewerId === post.userId
								? "You can't like your own post"
								: undefined
						}
						onAct={() => act(() => actions.like(post))}
					/>
					{onOpen ? (
						<button
							type="button"
							data-testid="alkanes-post-open-thread"
							onClick={() => onOpen(post, true)}
							aria-label={`Reply · ${post.numReplies ?? 0} replies`}
							className="-m-1 inline-flex items-center rounded-md p-1 transition-colors hover:bg-secondary/60 [&_*]:hover:text-foreground"
						>
							<Count icon={MessageCircle} value={post.numReplies} label="replies" />
						</button>
					) : (
						<Count icon={MessageCircle} value={post.numReplies} label="replies" />
					)}
				</div>
				{rowError && (
					<p role="alert" className="mt-1.5 text-xs text-destructive-ink">
						{rowError}
					</p>
				)}
			</div>
		</li>
	)
}

/// Bring `el` into view by moving ONLY the container that actually scrolls.
/// `scrollIntoView` also scrolls `overflow: hidden` ancestors, which shoved
/// the whole app shell (fixed header included) up the page.
function scrollWithin(el: HTMLElement, place: 'top' | 'center') {
	const rect = el.getBoundingClientRect()
	let parent = el.parentElement
	while (parent) {
		const overflowY = window.getComputedStyle(parent).overflowY
		if ((overflowY === 'auto' || overflowY === 'scroll') && parent.scrollHeight > parent.clientHeight) {
			const box = parent.getBoundingClientRect()
			const offset = place === 'top' ? 0 : (box.height - rect.height) / 2
			parent.scrollTop += rect.top - box.top - offset
			return
		}
		parent = parent.parentElement
	}
	// The page itself scrolls: clear the app's fixed 3.5rem top bar.
	const offset = place === 'top' ? 64 : (window.innerHeight - rect.height) / 2
	window.scrollBy({ top: rect.top - offset })
}

// ── one post's conversation ────────────────────────────────────────────────

/// Replies to one reply, previewed under it the way Twetch does: the first
/// couple inline on the conversation line, the rest one tap away.

/// One conversation, laid out the way Twetch lays it out: the posts this one
/// answers stacked above it on a connecting line, the post itself, the reply
/// box, then its replies — each with its own replies previewed beneath. Any
/// post in view opens as the new focus; Back unwinds to the row that was left.
function PostThread({
	post,
	lookups,
	now,
	depth,
	focusReply,
	onBack,
	onOpen
}: {
	post: TwetchPost
	lookups: FeedLookups
	now: number
	/// How many conversations deep the reader is (1 = opened from the feed).
	depth: number
	/// Opened through a reply control: put the cursor in the reply box.
	focusReply: boolean
	onBack: () => void
	onOpen: (post: TwetchPost, lookups: FeedLookups, focusReply?: boolean) => void
}) {
	const leadRef = React.useRef<HTMLUListElement | null>(null)

	// The conversation this post answers — oldest first.
	const { data: chain } = useSwr<{ ancestors?: TwetchPost[]; users?: Record<string, TwetchUser> }>(
		post.replyPostId ? `alkanes-twetch-thread-${post.id}` : null,
		() => twetchJson(`/v1/posts/${post.id}/thread`),
		{ revalidateOnFocus: false }
	)
	const ancestors = chain?.ancestors ?? []
	const ancestorLookups = React.useMemo<FeedLookups>(
		() => ({ ...lookups, users: { ...lookups.users, ...(chain?.users ?? {}) } }),
		[lookups, chain]
	)

	// Land on the post itself, not on the top of a long ancestor chain.
	const ancestorCount = ancestors.length
	React.useEffect(() => {
		if (leadRef.current) scrollWithin(leadRef.current, 'top')
	}, [post.id, ancestorCount])

	// After the reader's own reply the refetch must skip the browser's HTTP
	// cache, or it answers with the page from before they replied.
	const freshRef = React.useRef(false)
	const { data, error, isLoading, mutate } = useSwr<TwetchFeedPage>(
		`alkanes-twetch-replies-${post.id}`,
		() =>
			twetchJson<TwetchFeedPage>(
				`/v1/posts/${post.id}/replies`,
				freshRef.current ? { cache: 'reload' } : undefined
			),
		{ revalidateOnFocus: false }
	)
	const onReplied = () => {
		freshRef.current = true
		void mutate()
		window.setTimeout(() => void mutate(), 4_000)
	}
	const replies = React.useMemo(() => (data ? mergeFeedPages([data]) : null), [data])
	const replyLookups = React.useMemo<FeedLookups | null>(
		() =>
			replies
				? {
						users: { ...lookups.users, ...replies.users },
						replyPosts: replies.replyPosts,
						quotes: replies.quotes,
						quotedPosts: replies.quotedPosts,
						mediaDims: replies.mediaDims
				  }
				: null,
		[replies, lookups]
	)
	const author = ancestorLookups.users[String(post.userId)]?.name ?? `#${post.userId}`
	const replyCount = replies?.posts.length ?? post.numReplies ?? 0

	return (
		<div data-testid="alkanes-post-thread">
			<div className="sticky top-0 z-10 flex h-12 items-center gap-1 border-b border-border/50 bg-card/95 px-2 backdrop-blur">
				<button
					type="button"
					data-testid="alkanes-thread-back"
					onClick={onBack}
					aria-label={depth > 1 ? 'Back to the previous post' : 'Back to the feed'}
					className="alkanes-touch grid size-10 place-items-center rounded-full text-muted-foreground transition-colors hover:bg-secondary/60 hover:text-foreground"
				>
					<ArrowLeft className="size-5" aria-hidden="true" />
				</button>
				<h3 className="text-sm font-bold">Thread</h3>
				<span className="ml-auto pr-2 text-xs tabular-nums text-muted-foreground">
					{replyCount} {replyCount === 1 ? 'reply' : 'replies'}
				</span>
			</div>

			{ancestors.length > 0 && (
				<ul data-testid="alkanes-thread-ancestors">
					{ancestors.map((ancestor) => (
						<PostRow
							key={ancestor.id}
							post={ancestor}
							feed={ancestorLookups}
							now={now}
							threadLine
							hideReplyingTo
							onOpen={(next) => onOpen(next, ancestorLookups)}
						/>
					))}
				</ul>
			)}

			<ul ref={leadRef} className="border-b border-border/50">
				<PostRow post={post} feed={ancestorLookups} now={now} lead hideReplyingTo={ancestors.length > 0} />
			</ul>

			{/* Replying is an ordinary sponsored post with a parent: signed by
			    the wallet, fee paid by Twetch, free for the author. */}
			<div className="border-b border-border/50 px-3 py-2.5">
				<TwetchDoor
					key={post.id}
					compact
					autoFocus={focusReply}
					replyTo={{ postId: post.id, txid: post.txid ?? null, author }}
					onPosted={onReplied}
				/>
			</div>

			{isLoading ? (
				<>
					<GhostPost />
					<GhostPost />
				</>
			) : error && !data ? (
				<div className="px-4 py-8 text-center">
					<p className="text-sm font-semibold">Replies can&rsquo;t be reached</p>
					<button
						type="button"
						onClick={() => void mutate()}
						className="alkanes-touch mt-3 rounded-xl bg-secondary px-4 py-2 text-xs font-semibold transition-colors hover:bg-secondary/70"
					>
						Retry
					</button>
				</div>
			) : replies && replyLookups && replies.posts.length > 0 ? (
				<ul data-testid="alkanes-thread-replies" className="divide-y divide-border/50">
					{/* Flat, the way Twetch reads: every reply at one level, each
					    carrying its own reply count. Previewing a reply's replies
					    inline indented the list again at every step, and a thread
					    three deep had no width left to read in. Tapping one makes
					    it the thread's subject instead. */}
					{replies.posts.map((reply) => (
						<li key={reply.id} className="list-none">
							<PostRow
								post={reply}
								feed={replyLookups}
								now={now}
								hideReplyingTo
								onOpen={(next, viaReply) => onOpen(next, replyLookups, viaReply)}
							/>
						</li>
					))}
				</ul>
			) : (
				<p
					data-testid="alkanes-thread-empty"
					className="px-4 py-8 text-center text-xs text-muted-foreground"
				>
					No replies yet. Be the first to answer {author}.
				</p>
			)}
		</div>
	)
}

// ── the feed list (shared with the desktop rail) ───────────────────────────

function readPostParam(): number | null {
	return typeof window === 'undefined' ? null : parsePostParam(window.location.search)
}

function writePostParam(postId: number | null, how: 'push' | 'replace'): void {
	const url = new URL(window.location.href)
	if (postId === null) url.searchParams.delete(POST_PARAM)
	else url.searchParams.set(POST_PARAM, String(postId))
	const next = `${url.pathname}${url.search}${url.hash}`
	if (how === 'push') window.history.pushState(window.history.state, '', next)
	else window.history.replaceState(window.history.state, '', next)
}

/// A post opened by link, with the lookups a row needs built from its own read.
async function fetchLinkedPost(
	postId: number
): Promise<{ post: TwetchPost; lookups: FeedLookups; focusReply: boolean }> {
	const body = await twetchJson<{
		post: TwetchPost
		author?: TwetchUser
		quotedPost?: TwetchPost | null
		quotedAuthor?: TwetchUser | null
	}>(`/v1/posts/${postId}`)
	const users: Record<string, TwetchUser> = {}
	for (const user of [body.author, body.quotedAuthor]) {
		if (user) users[String(user.id)] = user
	}
	const quoted = body.quotedPost
	return {
		post: body.post,
		lookups: {
			users,
			replyPosts: {},
			quotes: quoted ? { [String(body.post.id)]: quoted.id } : {},
			quotedPosts: quoted ? { [String(quoted.id)]: quoted } : {},
			mediaDims: {}
		},
		focusReply: false
	}
}

export function TwetchPostList({
	feed,
	linkable = false,
	showMarket = false,
	onThreadChange,
	emptyTitle = 'No Alkanes posts yet',
	emptyBody = 'Posts appear here when someone selects an Alkanes token cashtag on Twetch or OW.'
}: {
	/// What an empty feed says — each surface names its own subject.
	emptyTitle?: string
	emptyBody?: string
	feed: TwetchFeedData
	/// This list owns the page's `?post=<id>`: the open conversation is written
	/// to the URL and a link carrying one opens it. One list per page — a
	/// secondary monitor (the rail) must not fight the main feed for it.
	linkable?: boolean
	/// Name and link each post's market (for feeds that mix markets).
	showMarket?: boolean
	/// Told when a conversation takes over the list, so a host page can put
	/// its own composer away instead of stacking two reply boxes.
	onThreadChange?: (open: boolean) => void
}) {
	const sentinelRef = React.useRef<HTMLDivElement | null>(null)
	const { hasMore, loadingInitial, loadingMore, loadMore } = feed

	// State-held clock (pools-status pattern): a render-time Date.now() is
	// impure under the compiler; a once-a-minute tick keeps "4m ago" honest
	// without repainting per row.
	const [now, setNow] = React.useState(() => Date.now())
	React.useEffect(() => {
		const timer = setInterval(() => setNow(Date.now()), 60_000)
		return () => clearInterval(timer)
	}, [])

	// The open conversation, as a stack: a reply opened from a thread pushes,
	// Back pops, and the last pop lands on the row the reader left.
	const [thread, setThread] = React.useState<
		{ post: TwetchPost; lookups: FeedLookups; focusReply: boolean }[]
	>([])
	const threadOpen = thread.length > 0
	React.useEffect(() => {
		onThreadChange?.(threadOpen)
	}, [threadOpen, onThreadChange])
	// The open conversation lives in the URL (`?post=<id>`), so a thread can
	// be linked to — a notification, a shared link — and the browser's Back
	// closes it. The stack itself stays in memory: the URL names its top.
	const topId = thread[thread.length - 1]?.post.id ?? null
	const urlSync = React.useRef({ mounted: false, depth: 0 })
	React.useEffect(() => {
		const before = urlSync.current
		urlSync.current = { mounted: true, depth: thread.length }
		// The first run must not erase the `?post=` a link arrived with.
		if (!linkable || !before.mounted || readPostParam() === topId) {
			return
		}
		// Opening deeper is a new history entry; stepping back out is not —
		// otherwise the browser's Back would reopen what was just closed.
		writePostParam(topId, thread.length > before.depth ? 'push' : 'replace')
	}, [linkable, topId, thread.length])
	React.useEffect(() => {
		if (!linkable) return
		let alive = true
		let first = true
		const sync = () => {
			let wanted = readPostParam()
			if (first && wanted === null) {
				// The page may have rewritten its query before this list mounted.
				wanted = takeLinkedPost()
				if (wanted !== null) writePostParam(wanted, 'replace')
			}
			first = false
			if (wanted === null) {
				setThread((stack) => (stack.length ? [] : stack))
				return
			}
			setThread((stack) => {
				const at = stack.findIndex((entry) => entry.post.id === wanted)
				if (at >= 0) return stack.slice(0, at + 1)
				void fetchLinkedPost(wanted).then(
					(entry) => alive && readPostParam() === wanted && setThread([entry]),
					// A dead link just shows the feed.
					() => alive && writePostParam(null, 'replace')
				)
				return stack
			})
		}
		sync()
		window.addEventListener('popstate', sync)
		return () => {
			alive = false
			window.removeEventListener('popstate', sync)
		}
	}, [linkable])

	const closeThread = () => {
		const leaving = thread[thread.length - 1]
		setThread((stack) => stack.slice(0, -1))
		if (leaving) {
			window.requestAnimationFrame(() => {
				const row = document.querySelector<HTMLElement>(`[data-post-id="${leaving.post.id}"]`)
				if (row) scrollWithin(row, 'center')
			})
		}
	}

	// Infinite scroll: when the sentinel under the last row becomes visible,
	// ask for the next cursor. IntersectionObserver instead of scroll math —
	// the list scrolls inside different containers on phone vs rail.
	React.useEffect(() => {
		const el = sentinelRef.current
		if (!el || !hasMore || loadingInitial) {
			return
		}
		const observer = new IntersectionObserver(
			(entries) => {
				if (entries.some((e) => e.isIntersecting) && !loadingMore) {
					loadMore()
				}
			},
			{ rootMargin: '600px 0px' }
		)
		observer.observe(el)
		return () => observer.disconnect()
	}, [hasMore, loadingInitial, loadingMore, loadMore])

	if (thread.length > 0) {
		const current = thread[thread.length - 1]
		return (
			<PostThread
				post={current.post}
				lookups={current.lookups}
				now={now}
				depth={thread.length}
				focusReply={current.focusReply}
				onBack={closeThread}
				onOpen={(post, lookups, focusReply = false) =>
					setThread((stack) =>
						// Re-opening the post already in focus (its own "more replies")
						// must not stack a duplicate.
						stack[stack.length - 1]?.post.id === post.id
							? stack
							: [...stack, { post, lookups, focusReply }]
					)
				}
			/>
		)
	}

	if (feed.loadingInitial) {
		return (
			<div data-testid="alkanes-social-loading">
				<GhostPost />
				<GhostPost />
				<GhostPost />
			</div>
		)
	}

	if (feed.failed) {
		return (
			<div data-testid="alkanes-social-error" className="px-4 py-8 text-center">
				<h3 className="text-sm font-semibold">The feed can&rsquo;t be reached</h3>
				<span className="mt-1 block text-xs text-muted-foreground">
					Twetch didn&rsquo;t answer. Nothing here is cached — retry when you&rsquo;re back online.
				</span>
				<button
					type="button"
					data-testid="alkanes-social-retry"
					onClick={feed.retry}
					className="alkanes-touch mt-3 rounded-xl bg-secondary px-4 py-2 text-xs font-semibold transition-colors hover:bg-secondary/70"
				>
					Retry
				</button>
			</div>
		)
	}

	if (feed.posts.length === 0) {
		return (
			<div data-testid="alkanes-social-empty" className="px-4 py-8 text-center">
				<h3 className="text-sm font-semibold">{emptyTitle}</h3>
				<span className="mx-auto mt-1 block max-w-[40ch] text-xs leading-relaxed text-muted-foreground">
					{emptyBody}
				</span>
			</div>
		)
	}

	return (
		<>
			<ul data-testid="alkanes-social-list" className="divide-y divide-border/50">
				{feed.posts.map((post) => (
					<PostRow
						key={post.id}
						post={post}
						feed={feed}
						now={now}
						showMarket={showMarket}
						onOpen={(opened, viaReply = false) =>
							setThread([{ post: opened, lookups: feed, focusReply: viaReply }])
						}
					/>
				))}
			</ul>
			<div ref={sentinelRef} aria-hidden="true" />
			{loadingMore && !feed.loadingInitial && <GhostPost />}
			{!hasMore && (
				<span className="block p-4 text-center text-xxs text-muted-foreground">
					You&rsquo;re caught up.
				</span>
			)}
		</>
	)
}

// ── the tab ────────────────────────────────────────────────────────────────

/// Posts from the people the connected wallet's Twetch account follows.
function FollowingFeed() {
	const identity = useTwetch((st) => st.identity)
	const address = Wallet.getTaprootAddress()
	const uid =
		identity && identity.address.toLowerCase() === (address ?? '').toLowerCase()
			? identity.uid
			: null
	const feed = useTwetchFeed(uid !== null, null, uid)

	if (uid === null) {
		return (
			<div
				data-testid="alkanes-social-following-placeholder"
				className="mx-4 mt-4 rounded-2xl bg-card px-5 py-6 text-center"
			>
				<h2 className="text-base font-semibold">Your Following feed</h2>
				<span className="mx-auto mt-1.5 block max-w-[46ch] text-sm leading-relaxed text-muted-foreground">
					Follow people from their name or profile and their posts collect here. It starts once this
					wallet has a Twetch profile — set one up, or publish a post.
				</span>
			</div>
		)
	}
	if (!feed.loadingInitial && !feed.failed && feed.posts.length === 0) {
		return (
			<div data-testid="alkanes-social-following-empty" className="px-4 py-8 text-center">
				<h3 className="text-sm font-semibold">Nobody followed yet</h3>
				<span className="mx-auto mt-1 block max-w-[44ch] text-xs leading-relaxed text-muted-foreground">
					Hover a name in the Alkanes feed and press Follow. It&rsquo;s free, and their posts show up
					here.
				</span>
			</div>
		)
	}
	return <TwetchPostList feed={feed} />
}

export default function SocialFeed() {
	const [tab, setTab] = React.useState<'alkanes' | 'following'>('alkanes')
	const feed = useTwetchFeed(tab === 'alkanes')
	const [threadOpen, setThreadOpen] = React.useState(false)

	return (
		<section aria-label="Social feed" data-testid="alkanes-social" className="flex flex-col">
			<div role="tablist" aria-label="Social feed tabs" className="flex border-b border-border">
				{(
					[
						['alkanes', 'Alkanes'],
						['following', 'Following']
					] as const
				).map(([id, label]) => {
					const on = tab === id
					return (
						<button
							key={id}
							type="button"
							role="tab"
							aria-selected={on}
							data-testid={`alkanes-social-tab-${id}`}
							onClick={() => setTab(id)}
							className={cn(
								'relative flex-1 py-3 text-[15px] font-semibold transition-colors',
								on ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'
							)}
						>
							{label}
							{on && (
								<span
									aria-hidden="true"
									className="absolute inset-x-10 bottom-0 h-0.5 rounded-full bg-primary"
								/>
							)}
						</button>
					)
				})}
			</div>

			{tab === 'alkanes' ? (
				<>
					{!threadOpen && <TwetchDoor onPosted={feed.retry} />}
					<div className={threadOpen ? undefined : 'mt-2'}>
						<TwetchPostList feed={feed} linkable onThreadChange={setThreadOpen} />
					</div>
				</>
			) : (
				<FollowingFeed />
			)}
		</section>
	)
}
